import React, { CSSProperties, FunctionComponent, useMemo } from 'react';

import Cta from '@gojob/ui-bits/Cta';
import Flex from '@gojob/ui-bits/Flex';
import useTheme from '@gojob/ui-bits/styles/useTheme';
import Text from '@gojob/ui-bits/Text';

import Link from 'src/components/Link/Link';
import LiveJobPreview from 'src/components/RelatedOffers/OfferList/LiveJobPreview';
import useTrackOffersCtaClick from 'src/tracking/useTrackOffersCtaClick';
import useTranslation from 'src/translations/useTranslation';

export type OfferListProps = {
  title?: string;
  label?: string;
  to?: string;
  jobIds?: (string | undefined)[];
  loading?: boolean;
  error?: boolean;
  className?: string;
  style?: CSSProperties;
};

const useStyles = () => {
  const { breakpoint, spacing } = useTheme();

  return {
    item: {
      margin: spacing(1),
    },
    linkContainer: {
      width: '100%',
      maxWidth: breakpoint.values.sm,
      alignSelf: 'center',
    },
  } as const;
};

export const OFFER_LIST_LENGTH = 3;

const OfferList: FunctionComponent<OfferListProps> = (props) => {
  const { jobIds, title, label, loading, to = '#' } = props;

  const classes = useStyles();
  const t = useTranslation();

  const trackOffersCtaClick = useTrackOffersCtaClick();

  const ids = useMemo(() => {
    if (loading) {
      /** Here, we must explitely cast the generated array */
      return Array.apply(null, Array(OFFER_LIST_LENGTH)) as undefined[];
    }
    return jobIds;
  }, [loading, jobIds]);

  if (!ids?.length) {
    return null;
  }
  return (
    <Flex flexDirection="column" alignItems="stretch" margin={[4, 0]}>
      <Text variant="headline2" as="h2" gutter={2}>
        {title}
      </Text>

      <Flex flexDirection="column">
        {ids?.slice(0, OFFER_LIST_LENGTH).map((id, index) => {
          return (
            <LiveJobPreview key={id || index} loading={loading} css={classes.item} index={index} jobId={id} />
          );
        })}
      </Flex>

      <Flex css={classes.linkContainer} flexDirection="column" margin={[4, 0, 2]}>
        <Cta as={Link} to={to} onClick={trackOffersCtaClick} variant="secondary">
          {label || t('see_all_offers')}
        </Cta>
      </Flex>
    </Flex>
  );
};

export default OfferList;
